import { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { Grid } from "@mui/material";
import Button from "@mui/material/Button";
import { v4 as uuid } from "uuid";

import { OmniStringToDate } from "../../utils/helpers";
import OmniDatePicker from "../ui/omniDatePicker";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogMessage from "../ui/omniDialogMessage";
import DialogTitle from "../ui/omniDialogTitle";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import AlertDialog from "./alertDialog";

const PatientConditionDialog = props => {
  const {
    classes,
    open,
    dialogTitle = "Patient Medication",
    dialogMessage = null,
    okMessage = "Ok",
    cancelMessage = "Cancel",
    maxWidth = "xs",
    fullWidth = true,
    rowCount = 1,
    allConditions,
    deleteMessage = "remove",
    condition = "",
  } = props;

  const [showConfirm, setShowConfirm] = useState(false);

  const [originalConditionName] = useState(condition);
  const [conditionName, setConditionName] = useState(condition);
  const [conditionDiagnosed, setConditionDiagnosed] = useState(
    condition === "" ? new Date() : OmniStringToDate(allConditions[condition].diagnosed),
  );
  const [conditionNote, setConditionNote] = useState(
    condition === "" ? "" : allConditions[condition].note,
  );

  const onDeleteHandler = () => {
    if (originalConditionName !== "") {
      delete allConditions[originalConditionName];
    }
    props.onAccept(allConditions);
  };

  const onAcceptHandler = () => {
    if (conditionName !== originalConditionName && originalConditionName !== "") {
      delete allConditions[originalConditionName];
    }
    allConditions[conditionName] = {
      diagnosed: conditionDiagnosed,
      note: conditionNote,
    };
    props.onAccept(allConditions);
  };

  const onCloseHandler = () => {
    props.onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        {dialogMessage && (
          <DialogMessage className={classes.description}>{dialogMessage}</DialogMessage>
        )}
        <OmniTextField
          label={"Diagnosis"}
          type="textInput"
          name={uuid()}
          variant="outlined"
          value={conditionName}
          onChange={event => setConditionName(event.target.value)}
          autofill="off"
          autoFocus
          placeholder={"Enter Diagnosis"}
        />

        <OmniDatePicker
          label="Diagnosis Date"
          value={conditionDiagnosed}
          name={uuid()} //prevents AutoComplete Suggestions
          variant="inline"
          inputVariant="outlined"
          onAccept={null}
          onChange={date => setConditionDiagnosed(date)}
          format="MM/dd/yyyy"
          fullWidth
        />
        <OmniTextField
          label={"Notes"}
          type="textInput"
          name={uuid()}
          variant="outlined"
          value={conditionNote}
          onChange={event => setConditionNote(event.target.value)}
          autofill="off"
          multiline
          rows={rowCount}
        />
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent="space-between">
          <Grid item xs={6}>
            {originalConditionName !== "" && (
              <OmniResponsiveButton
                icon={<DeleteIcon />}
                onClick={() => setShowConfirm(true)}
                buttonText={deleteMessage}
                tooltipTitle=""
              />
            )}
          </Grid>
          <Grid container item justifyContent="flex-end" xs={6} spacing={1}>
            <Grid item>
              {cancelMessage && (
                <Button onClick={onCloseHandler} color="secondary" variant="contained">
                  {cancelMessage}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button
                onClick={onAcceptHandler}
                color="secondary"
                disabled={conditionName == null || conditionName.length < 1}
                variant="contained"
              >
                {okMessage}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to remove this condition?"}
          onAccept={() => {
            onDeleteHandler();
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
    </Dialog>
  );
};

export default PatientConditionDialog;
